import { __rest } from 'tslib';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import styled from '@emotion/styled';
import React, { createContext, useContext, useState } from 'react';
import { MAPPING_ALIGNMENT_FLEX } from './constants.es.js';
import { getStyle } from './utils.es.js';
import { CloseOutlined, MenuOutlined } from '@teko-builder/icons';
import { d as getPropsIcon } from './common-dbaafe9f.js';
import { MenuItemType } from '@teko-builder/types/pb';
import { H as Heading } from './Heading-e29a867a.js';
import { a as ButtonV2 } from './ButtonV2-6506d594.js';
import { I as Image } from './Image-b56e679c.js';

const HeaderContext = createContext({});

const Header = React.forwardRef((_a, ref) => {
    var { children, isMobile, toggle } = _a, rest = __rest(_a, ["children", "isMobile", "toggle"]);
    const { commonStyle } = rest, others = __rest(rest, ["commonStyle"]);
    const { maxWidth } = commonStyle, restStyle = __rest(commonStyle, ["maxWidth"]);
    return (jsx(Wrapper$2, Object.assign({ ref: ref, isMobile: isMobile, commonStyle: restStyle }, others, { children: jsx(ContentRow, Object.assign({ style: { maxWidth } }, { children: jsx(HeaderContext.Provider, Object.assign({ value: { toggle, isMobile } }, { children: children })) })) })));
});
Header.displayName = 'Header';
const mobileCss = ({ commonStyle: { maxHeight, height, minHeight }, }) => `
  max-width: 100%;

  .logo {
    max-width: 50%;
  }

  .menu {
    &-desktop {
      display: none !important;
    }

    &-col {
      flex-direction: column;
      row-gap: 8px;
    }

    &-item {
      &.button {
        justify-content: center;
      }
    }
  }

  .toggle-button {
    ${getStyle({ maxHeight, height, minHeight })}
  }
`;
const desktopCss = ({ alignment }) => `
  .logo {
    ${alignment === 'center' ? 'flex: 1;' : ''}
  }

  .menu {
    &-desktop {
      ${alignment === 'center' ? 'flex: 2;' : 'flex: 1;'}
    }

    &-col {
      &.left {
        justify-content: ${MAPPING_ALIGNMENT_FLEX[alignment]};
      }
      &.right {
        ${alignment === 'center' ? 'flex: 1;' : ''}
      }
    }
  }

  .dropdown-menu,
  .toggle-button {
    display: none !important;
  }
`;
const Wrapper$2 = styled.div((props) => {
    const { isMobile, commonStyle } = props;
    return [
        `
      ${getStyle(commonStyle)}
      ${(commonStyle === null || commonStyle === void 0 ? void 0 : commonStyle.position) === 'sticky' ? 'top: 0; z-index: 100;' : ''}
      display: flex;
      justify-content: center;
      margin-left: auto;
      margin-right: auto;
    `,
        isMobile && mobileCss,
        isMobile === false && desktopCss,
        isMobile === undefined &&
            `
        @media (max-width: 767px) {
          ${mobileCss(props)}
        }
        @media (min-width: 767px) {
          ${desktopCss(props)}
        }
      `,
    ];
});
const ContentRow = styled.div `
  background: inherit;
  width: 100%;
  display: flex;
  align-items: center;
  column-gap: 16px;
`;

const DesktopMenu = (props) => {
    return jsx(Wrapper$1, Object.assign({ className: "menu-desktop" }, props));
};
const Wrapper$1 = styled.div `
  display: flex;
  align-items: center;
  justify-content: flex-end;
  column-gap: 24px;
`;

const MobileMenu = ({ children }) => {
    const { toggle } = useContext(HeaderContext);
    const [visibleMenu, setVisibleMenu] = useState(false);
    return (jsxs(Fragment, { children: [jsx(ToggleButton, Object.assign({ className: "toggle-button", onClick: () => setVisibleMenu(!visibleMenu) }, { children: visibleMenu ? (jsx(CloseOutlined, Object.assign({}, getPropsIcon(toggle === null || toggle === void 0 ? void 0 : toggle.icon)))) : (jsx(MenuOutlined, Object.assign({}, getPropsIcon(toggle === null || toggle === void 0 ? void 0 : toggle.icon)))) })), jsx(DropdownMenu, Object.assign({ className: `dropdown-menu ${visibleMenu ? 'show' : ''}` }, { children: children }))] }));
};
const ToggleButton = styled.div `
  margin-left: auto;
  cursor: pointer;
  display: flex;
  align-items: center;
  padding: 0 8px;
`;
const DropdownMenu = styled.div `
  position: absolute;
  top: 80%;
  left: 0;
  right: 0;
  z-index: 100;

  display: flex;
  flex-direction: column;
  align-content: center;
  row-gap: 8px;

  height: auto;
  padding: 16px;
  overflow-y: auto;

  opacity: 0;
  visibility: hidden;
  transition: all 0.2s ease-in-out;

  background: inherit;
  box-shadow: 0 10px 16px rgba(10, 10, 10, 0.1);

  &.show {
    top: 100%;
    opacity: 1;
    visibility: visible;
  }
`;

const Menu = ({ children }) => {
    const { isMobile } = useContext(HeaderContext);
    if (isMobile !== undefined) {
        if (isMobile) {
            return jsx(MobileMenu, { children: children });
        }
        return jsx(DesktopMenu, { children: children });
    }
    return (jsxs(Fragment, { children: [jsx(MobileMenu, { children: children }), jsx(DesktopMenu, { children: children })] }));
};
Menu.displayName = 'Menu';

const MenuCol = React.forwardRef((_a, ref) => {
    var { type = 'right' } = _a, rest = __rest(_a, ["type"]);
    if (type === 'left') {
        return jsx(LeftMenuCol, Object.assign({ ref: ref }, rest, { className: "menu-col left" }));
    }
    return jsx(RightMenuCol, Object.assign({ ref: ref }, rest, { className: "menu-col right" }));
});
MenuCol.displayName = 'menuCol';
const menuColCss = `
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  column-gap: 24px;
  row-gap: 16px;
`;
const RightMenuCol = styled.div(menuColCss, `
    justify-content: flex-end;
  `);
const LeftMenuCol = styled.div(menuColCss, `
  flex: 1;
`);

const MenuItem = React.forwardRef((props, ref) => {
    const { itemData, type, children } = props, rest = __rest(props, ["itemData", "type", "children"]);
    if (children)
        return children;
    if (type === MenuItemType.link) {
        return (jsx("div", Object.assign({ ref: ref }, { children: jsx(Heading, Object.assign({}, itemData, rest)) })));
    }
    return (jsx("div", Object.assign({ ref: ref }, { children: jsx(ButtonV2, Object.assign({ ref: ref }, itemData, rest, { className: "menu-item button" })) })));
});
MenuItem.displayName = 'MenuItem';

const Logo = React.forwardRef((props, ref) => {
    return (jsx(Wrapper, Object.assign({ ref: ref, className: "logo" }, { children: jsx(Image, Object.assign({}, props)) })));
});
Logo.displayName = 'Logo';
const Wrapper = styled.div `
  max-width: 33%;
`;

export { Header as H, Logo as L, Menu as M, MenuCol as a, MenuItem as b };
